import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import useMobileView from '../../hooks/useMobileView';
import {
  Box,
  Fab,
  TextField,
  Button,
  Typography,
  IconButton,
  Tooltip,
  useTheme,
  alpha,
  Paper,
  Avatar,
  CircularProgress,
  Backdrop,
  Slide,
  InputAdornment,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import SendIcon from '@mui/icons-material/Send';
import PersonIcon from '@mui/icons-material/Person';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import PhoneIcon from '@mui/icons-material/Phone';
import TitleIcon from '@mui/icons-material/Title';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

const ACCENT = '#4ce1b6';
const ACCENT_DEEP = '#1fa88a';
const SUPPORT_API = '/api/support/complaint/';
const FAB_SIZE = 56;
const GAP = 10;
const PANEL_WIDTH = 400;

function buildInitialForm() {
  if (typeof window === 'undefined') {
    return {
      heading: '',
      email: '',
      phone: '',
      description: '',
      website: '',
    };
  }
  const ctx = window.DJANGO_CONTEXT || {};
  return {
    heading: '',
    email: (ctx.user && ctx.user.email) || '',
    phone: '',
    description: '',
    website: '',
  };
}

/** Wave SVG — fills with body background to separate gradient header from content */
function HeaderWave({ fill }) {
  return (
    <Box sx={{ lineHeight: 0, mt: -0.5 }}>
      <svg
        viewBox="0 0 400 28"
        preserveAspectRatio="none"
        aria-hidden
        style={{ display: 'block', width: '100%', height: 22 }}
      >
        <path
          d="M0,8 C80,24 120,0 200,10 C280,20 320,2 400,14 L400,28 L0,28 Z"
          fill={fill}
        />
      </svg>
    </Box>
  );
}

/**
 * Anchored support widget above the FAB (chat-widget style) — complaint form + email to RoomNexa.
 * On NexaStay booking engine (`/nexastay/...` or body.nexastay), copy references a Nexa Stay support ticket.
 */
export default function SupportChatFab() {
  const theme = useTheme();
  const { isCompact } = useMobileView();
  const isDark = theme.palette.mode === 'dark';
  const location = useLocation();
  const [isNexaStayBookingEngine, setIsNexaStayBookingEngine] = useState(() =>
    typeof window !== 'undefined' ? window.location.pathname.startsWith('/nexastay') : false
  );

  useEffect(() => {
    const t = setTimeout(() => {
      try {
        const path = window.location.pathname || '';
        const bodyNs = typeof document !== 'undefined' && document.body?.classList?.contains('nexastay');
        setIsNexaStayBookingEngine(bodyNs || path.startsWith('/nexastay'));
      } catch (_) {
        setIsNexaStayBookingEngine((location.pathname || '').startsWith('/nexastay'));
      }
    }, 0);
    return () => clearTimeout(t);
  }, [location.pathname]);

  const supportCopy = useMemo(() => {
    if (isNexaStayBookingEngine) {
      return {
        headerTitle: 'NexaStay support',
        formSubtitle: 'We’ll open a Nexa Stay support ticket from your details below and reply by email.',
        successSubtitle: 'Your Nexa Stay support ticket is logged — check your inbox.',
        agentLabel: 'Nexa Stay',
        footer: 'Nexa Stay · booking engine support',
        submitButton: 'Submit support ticket',
        fabOpenLabel: 'Close Nexa Stay support',
        fabClosedLabel: 'Nexa Stay support',
        fabClosedTooltip: 'Nexa Stay support · chat with us',
      };
    }
    return {
      headerTitle: 'Chat with us!',
      formSubtitle: 'We usually reply by email within one business day.',
      successSubtitle: 'You’re all set — check your inbox.',
      agentLabel: 'RoomNexa',
      footer: 'RoomNexa support',
      submitButton: 'Submit & start chat',
      fabOpenLabel: 'Close support',
      fabClosedLabel: 'Help & support',
      fabClosedTooltip: 'Help & support',
    };
  }, [isNexaStayBookingEngine]);

  const [open, setOpen] = useState(false);
  const [step, setStep] = useState('form');
  const [submitting, setSubmitting] = useState(false);
  const [ticketId, setTicketId] = useState('');
  const [form, setForm] = useState(() => buildInitialForm());
  const [fieldErrors, setFieldErrors] = useState({});
  const [submittedSnapshot, setSubmittedSnapshot] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const dragStateRef = useRef({
    active: false,
    moved: false,
    startClientX: 0,
    startClientY: 0,
    startOffsetX: 0,
    startOffsetY: 0,
  });
  const suppressFabClickRef = useRef(false);

  const bodyBg = isDark ? '#2a2a31' : '#ffffff';
  const borderSubtle = isDark ? '#333246' : '#e8eaed';
  const fieldSlot = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 2,
      backgroundColor: isDark ? alpha('#fff', 0.04) : alpha('#000', 0.02),
      '& fieldset': { borderColor: isDark ? alpha('#fff', 0.12) : alpha('#000', 0.08) },
      '&:hover fieldset': { borderColor: alpha(ACCENT, 0.45) },
      '&.Mui-focused fieldset': { borderColor: ACCENT, borderWidth: 2 },
    },
  };

  const resetAll = useCallback(() => {
    setStep('form');
    setTicketId('');
    setFieldErrors({});
    setSubmittedSnapshot(null);
    setForm(buildInitialForm());
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
    setTimeout(resetAll, 280);
  }, [resetAll]);

  const handleOpen = useCallback(() => {
    setForm(buildInitialForm());
    setFieldErrors({});
    setStep('form');
    setSubmittedSnapshot(null);
    setOpen(true);
  }, []);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setFieldErrors((prev) => ({ ...prev, [name]: undefined, _: undefined }));
  }, []);

  const handleSubmit = useCallback(async () => {
    setFieldErrors({});
    setSubmitting(true);
    try {
      const ctx = typeof window !== 'undefined' ? window.DJANGO_CONTEXT || {} : {};
      const path = typeof window !== 'undefined' ? window.location.pathname || '' : '';
      const onLanding =
        path === '/onboarding/landing' || path.startsWith('/onboarding/landing/');
      const ctxHotelName = ((ctx.selectedHotel && ctx.selectedHotel.name) || '').trim();
      let hotelName =
        onLanding && !ctxHotelName ? '' : ctxHotelName;
      const orgName = ((ctx.userOrganisation && ctx.userOrganisation.name) || '').trim();
      const nexaStayHotelMatch = path.match(/^\/nexastay\/hotel\/([^/]+)/);
      const nexaStayHotelId = nexaStayHotelMatch ? decodeURIComponent(nexaStayHotelMatch[1]) : '';
      if (isNexaStayBookingEngine) {
        hotelName = nexaStayHotelId
          ? `NexaStay booking engine · hotel ${nexaStayHotelId}`
          : 'NexaStay booking engine (guest site)';
      }
      const res = await fetch(SUPPORT_API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          heading: form.heading.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
          hotel_name: hotelName,
          org_name: orgName,
          description: form.description.trim(),
          website: form.website,
          source: isNexaStayBookingEngine ? 'nexastay_booking_engine' : 'roomnexa_app',
          page_path: path.slice(0, 500),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        const errs = data.errors || {};
        const flat = {};
        Object.keys(errs).forEach((k) => {
          flat[k] = Array.isArray(errs[k]) ? errs[k][0] : String(errs[k]);
        });
        if (Object.keys(flat).length) {
          setFieldErrors(flat);
        } else {
          setFieldErrors({ _: data.message || 'Something went wrong. Please try again.' });
        }
        return;
      }
      setTicketId(data.ticket_id || '');
      setSubmittedSnapshot({
        heading: form.heading.trim(),
        description: form.description.trim(),
        email: form.email.trim(),
      });
      setStep('chat');
    } catch {
      setFieldErrors({ _: 'Network error. Check your connection and try again.' });
    } finally {
      setSubmitting(false);
    }
  }, [form, isNexaStayBookingEngine]);

  const subtitle =
    step === 'form' ? supportCopy.formSubtitle : supportCopy.successSubtitle;

  const getFabBasePosition = useCallback(() => {
    const marginRight = window.innerWidth < 600 ? 16 : 24;
    const marginBottom = window.innerWidth < 600 ? 16 : 24;
    return {
      x: window.innerWidth - marginRight - FAB_SIZE,
      y: window.innerHeight - marginBottom - FAB_SIZE,
    };
  }, []);

  const clampDragOffset = useCallback((x, y) => {
    if (typeof window === 'undefined') return { x, y };
    const base = getFabBasePosition();
    const minX = 8;
    const minY = 8;
    const maxX = window.innerWidth - FAB_SIZE - 8;
    const maxY = window.innerHeight - FAB_SIZE - 8;
    const nextX = Math.min(maxX, Math.max(minX, base.x + x));
    const nextY = Math.min(maxY, Math.max(minY, base.y + y));
    return {
      x: nextX - base.x,
      y: nextY - base.y,
    };
  }, [getFabBasePosition]);

  const handleDragMove = useCallback((event) => {
    const state = dragStateRef.current;
    if (!state.active) return;
    let clientX;
    let clientY;
    if ('touches' in event) {
      if (!event.touches.length) return;
      clientX = event.touches[0].clientX;
      clientY = event.touches[0].clientY;
      event.preventDefault();
    } else {
      clientX = event.clientX;
      clientY = event.clientY;
    }
    const deltaX = clientX - state.startClientX;
    const deltaY = clientY - state.startClientY;
    if (Math.abs(deltaX) > 3 || Math.abs(deltaY) > 3) {
      state.moved = true;
    }
    setDragOffset(clampDragOffset(state.startOffsetX + deltaX, state.startOffsetY + deltaY));
  }, [clampDragOffset]);

  const endDrag = useCallback(() => {
    const state = dragStateRef.current;
    if (!state.active) return;
    if (state.moved) {
      suppressFabClickRef.current = true;
    }
    state.active = false;
    window.removeEventListener('mousemove', handleDragMove);
    window.removeEventListener('mouseup', endDrag);
    window.removeEventListener('touchmove', handleDragMove);
    window.removeEventListener('touchend', endDrag);
    window.removeEventListener('touchcancel', endDrag);
  }, [handleDragMove]);

  const beginDrag = useCallback((event) => {
    if (event.button !== undefined && event.button !== 0) return;
    let clientX;
    let clientY;
    if ('touches' in event) {
      if (!event.touches.length) return;
      clientX = event.touches[0].clientX;
      clientY = event.touches[0].clientY;
    } else {
      clientX = event.clientX;
      clientY = event.clientY;
    }
    dragStateRef.current = {
      active: true,
      moved: false,
      startClientX: clientX,
      startClientY: clientY,
      startOffsetX: dragOffset.x,
      startOffsetY: dragOffset.y,
    };
    window.addEventListener('mousemove', handleDragMove);
    window.addEventListener('mouseup', endDrag);
    window.addEventListener('touchmove', handleDragMove, { passive: false });
    window.addEventListener('touchend', endDrag);
    window.addEventListener('touchcancel', endDrag);
  }, [dragOffset.x, dragOffset.y, endDrag, handleDragMove]);

  useEffect(() => {
    const handleResize = () => {
      setDragOffset((prev) => clampDragOffset(prev.x, prev.y));
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      endDrag();
    };
  }, [clampDragOffset, endDrag]);

  const handleFabClick = useCallback(() => {
    if (suppressFabClickRef.current) {
      suppressFabClickRef.current = false;
      return;
    }
    if (open) {
      handleClose();
    } else {
      handleOpen();
    }
  }, [open, handleClose, handleOpen]);

  if ((location.pathname || '').startsWith('/pos-restaurant/open')) {
    return null;
  }

  if (isCompact) {
    return null;
  }

  const zFab = theme.zIndex.drawer + 52;
  const zPanel = theme.zIndex.drawer + 51;
  const zBackdrop = theme.zIndex.drawer + 50;
  const margin = 12;
  const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 1280;
  const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 800;
  const fabBasePosition = typeof window !== 'undefined'
    ? getFabBasePosition()
    : { x: viewportWidth - FAB_SIZE - 24, y: viewportHeight - FAB_SIZE - 24 };
  const fabPosition = {
    x: fabBasePosition.x + dragOffset.x,
    y: fabBasePosition.y + dragOffset.y,
  };
  const panelWidth = Math.min(PANEL_WIDTH, Math.max(0, viewportWidth - margin * 2));
  const panelMaxHeight = Math.min(620, Math.max(0, viewportHeight - margin * 2));
  const preferOpenRight = fabPosition.x < viewportWidth / 2;
  const preferOpenDown = fabPosition.y < viewportHeight / 2;
  let panelLeft = preferOpenRight
    ? fabPosition.x + FAB_SIZE + GAP
    : fabPosition.x - panelWidth - GAP;
  let panelTop = preferOpenDown
    ? fabPosition.y + FAB_SIZE + GAP
    : fabPosition.y - panelMaxHeight - GAP;
  panelLeft = Math.max(margin, Math.min(panelLeft, viewportWidth - panelWidth - margin));
  panelTop = Math.max(margin, Math.min(panelTop, viewportHeight - panelMaxHeight - margin));

  return (
    <>
      <Backdrop
        open={open}
        onClick={handleClose}
        sx={{
          zIndex: zBackdrop,
          backgroundColor: alpha('#0a0e14', isDark ? 0.55 : 0.35),
          backdropFilter: 'blur(2px)',
        }}
      />

      <Slide direction={preferOpenDown ? 'down' : 'up'} in={open} mountOnEnter unmountOnExit timeout={280}>
          <Paper
            elevation={0}
            onClick={(e) => e.stopPropagation()}
            sx={{
              position: 'fixed',
              left: panelLeft,
              top: panelTop,
              zIndex: zPanel,
              width: panelWidth,
              maxWidth: panelWidth,
              maxHeight: panelMaxHeight,
              display: 'flex',
              flexDirection: 'column',
              borderRadius: '20px',
              overflow: 'hidden',
              border: `1px solid ${alpha(ACCENT, isDark ? 0.35 : 0.4)}`,
              boxShadow: isDark
                ? `0 20px 50px rgba(0,0,0,0.55), 0 0 40px ${alpha(ACCENT, 0.12)}`
                : `0 20px 50px rgba(15, 23, 42, 0.12), 0 8px 24px ${alpha(ACCENT, 0.15)}`,
            }}
          >
            {/* Gradient header */}
            <Box
              sx={{
                position: 'relative',
                px: 2,
                pt: 2,
                pb: 0,
                background: `linear-gradient(135deg, ${ACCENT_DEEP} 0%, ${ACCENT} 42%, #5ee9c9 100%)`,
                color: '#fff',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                  <Avatar
                    sx={{
                      width: 48,
                      height: 48,
                      bgcolor: alpha('#fff', 0.22),
                      color: '#fff',
                      border: `2px solid ${alpha('#fff', 0.35)}`,
                      boxShadow: `0 4px 14px ${alpha('#000', 0.2)}`,
                    }}
                  >
                    <SupportAgentIcon sx={{ fontSize: 26 }} />
                  </Avatar>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 800,
                        fontSize: '1.05rem',
                        letterSpacing: '-0.02em',
                        color: '#000',
                      }}
                    >
                      {supportCopy.headerTitle}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontSize: '0.8125rem', mt: 0.25, color: '#000', opacity: 0.92 }}
                    >
                      {subtitle}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 0.25, mt: -0.5 }}>
                  <IconButton
                    size="small"
                    onClick={handleClose}
                    aria-label="Minimize"
                    sx={{ color: alpha('#fff', 0.9), '&:hover': { bgcolor: alpha('#fff', 0.12) } }}
                  >
                    <KeyboardArrowDownIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={handleClose}
                    aria-label="Close"
                    sx={{ color: alpha('#fff', 0.9), '&:hover': { bgcolor: alpha('#fff', 0.12) } }}
                  >
                    <CloseIcon />
                  </IconButton>
                </Box>
              </Box>
              <HeaderWave fill={bodyBg} />
            </Box>

            {/* Body */}
            <Box
              sx={{
                flex: 1,
                overflowY: 'auto',
                px: 2,
                pt: 1.5,
                pb: 1,
                bgcolor: bodyBg,
                backgroundImage: isDark
                  ? `radial-gradient(ellipse 120% 80% at 100% 0%, ${alpha(ACCENT, 0.07)}, transparent 50%)`
                  : `radial-gradient(ellipse 100% 60% at 100% 0%, ${alpha(ACCENT, 0.08)}, transparent 55%)`,
              }}
            >
              {fieldErrors._ && (
                <Typography color="error" variant="caption" sx={{ display: 'block', mb: 1.5 }}>
                  {fieldErrors._}
                </Typography>
              )}

              {step === 'form' && (
                <Box
                  component="form"
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleSubmit();
                  }}
                  noValidate
                >
                  <input
                    type="text"
                    name="website"
                    value={form.website}
                    onChange={handleChange}
                    tabIndex={-1}
                    autoComplete="off"
                    style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }}
                    aria-hidden
                  />

                  <TextField
                    name="heading"
                    label="Subject / heading"
                    value={form.heading}
                    onChange={handleChange}
                    required
                    fullWidth
                    size="small"
                    margin="dense"
                    error={Boolean(fieldErrors.heading)}
                    helperText={fieldErrors.heading}
                    inputProps={{ maxLength: 200 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <TitleIcon sx={{ fontSize: 20, color: alpha(ACCENT, 0.85) }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ mb: 1, ...fieldSlot }}
                  />

                  <TextField
                    name="email"
                    label="Email"
                    type="email"
                    value={form.email}
                    onChange={handleChange}
                    required
                    fullWidth
                    size="small"
                    margin="dense"
                    error={Boolean(fieldErrors.email)}
                    helperText={fieldErrors.email}
                    autoComplete="email"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailOutlinedIcon sx={{ fontSize: 20, color: alpha(ACCENT, 0.85) }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ mb: 1, ...fieldSlot }}
                  />

                  <TextField
                    name="phone"
                    label="Phone number"
                    value={form.phone}
                    onChange={handleChange}
                    required
                    fullWidth
                    size="small"
                    margin="dense"
                    error={Boolean(fieldErrors.phone)}
                    helperText={fieldErrors.phone}
                    autoComplete="tel"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PhoneIcon sx={{ fontSize: 20, color: alpha(ACCENT, 0.85) }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ mb: 1, ...fieldSlot }}
                  />

                  <TextField
                    name="description"
                    label="Describe your issue"
                    value={form.description}
                    onChange={handleChange}
                    required
                    fullWidth
                    size="small"
                    margin="dense"
                    multiline
                    minRows={3}
                    maxRows={8}
                    error={Boolean(fieldErrors.description)}
                    helperText={fieldErrors.description || `${form.description.length} / 8000`}
                    inputProps={{ maxLength: 8000 }}
                    sx={{ mb: 1, ...fieldSlot }}
                  />
                </Box>
              )}

              {step === 'chat' && submittedSnapshot && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 1.75,
                      alignSelf: 'flex-start',
                      maxWidth: '94%',
                      borderRadius: 2.5,
                      border: `1px solid ${alpha(ACCENT, 0.4)}`,
                      background: isDark ? alpha(ACCENT, 0.1) : alpha(ACCENT, 0.12),
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                      <Avatar sx={{ width: 26, height: 26, bgcolor: ACCENT, color: '#000' }}>
                        <SupportAgentIcon sx={{ fontSize: 16 }} />
                      </Avatar>
                      <Typography variant="subtitle2" fontWeight={800}>
                        {supportCopy.agentLabel}
                      </Typography>
                      {ticketId ? (
                        <Typography variant="caption" sx={{ ml: 'auto', fontWeight: 700, opacity: 0.85 }}>
                          {ticketId}
                        </Typography>
                      ) : null}
                    </Box>
                    <Typography variant="body2" sx={{ lineHeight: 1.55 }}>
                      {isNexaStayBookingEngine ? (
                        <>
                          Thanks — we’ve created your <strong>Nexa Stay support ticket</strong>
                          {ticketId ? (
                            <>
                              {' '}
                              (<strong>{ticketId}</strong>)
                            </>
                          ) : null}
                          . We’ll follow up at <strong>{submittedSnapshot.email}</strong>.
                        </>
                      ) : (
                        <>
                          Thanks — we’ve emailed support. We’ll follow up at{' '}
                          <strong>{submittedSnapshot.email}</strong>.
                        </>
                      )}
                    </Typography>
                  </Paper>

                  <Paper
                    elevation={0}
                    sx={{
                      p: 1.75,
                      alignSelf: 'flex-end',
                      maxWidth: '94%',
                      borderRadius: 2.5,
                      border: `1px solid ${borderSubtle}`,
                      bgcolor: isDark ? alpha('#fff', 0.04) : alpha('#000', 0.03),
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                      <Avatar sx={{ width: 26, height: 26, bgcolor: alpha(ACCENT, 0.25), color: isDark ? '#fff' : '#222' }}>
                        <PersonIcon sx={{ fontSize: 16 }} />
                      </Avatar>
                      <Typography variant="subtitle2" fontWeight={800}>
                        You
                      </Typography>
                    </Box>
                    <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                      {submittedSnapshot.heading}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                      {submittedSnapshot.description}
                    </Typography>
                  </Paper>
                </Box>
              )}
            </Box>

            {/* Footer */}
            <Box
              sx={{
                flexShrink: 0,
                px: 2,
                py: 1.5,
                borderTop: `1px solid ${borderSubtle}`,
                bgcolor: isDark ? alpha('#000', 0.2) : alpha(ACCENT, 0.04),
              }}
            >
              {step === 'form' ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Button
                    type="button"
                    fullWidth
                    variant="contained"
                    disabled={submitting}
                    onClick={handleSubmit}
                    endIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
                    sx={{
                      py: 1.1,
                      borderRadius: 999,
                      textTransform: 'none',
                      fontWeight: 800,
                      fontSize: '0.95rem',
                      background: `linear-gradient(135deg, ${ACCENT} 0%, ${ACCENT_DEEP} 100%)`,
                      color: '#000',
                      boxShadow: `0 6px 20px ${alpha(ACCENT, 0.45)}`,
                      '&:hover': {
                        background: `linear-gradient(135deg, ${alpha(ACCENT, 0.95)} 0%, ${ACCENT_DEEP} 100%)`,
                        boxShadow: `0 8px 24px ${alpha(ACCENT, 0.5)}`,
                      },
                    }}
                  >
                    {supportCopy.submitButton}
                  </Button>
                  <Typography variant="caption" textAlign="center" color="text.secondary" sx={{ display: 'block' }}>
                    {isNexaStayBookingEngine
                      ? 'By submitting, you agree we may contact you about this Nexa Stay support ticket.'
                      : 'By submitting, you agree we may contact you about this request.'}
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={handleClose}
                    sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 999, borderColor: borderSubtle }}
                  >
                    Close
                  </Button>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={() => {
                      resetAll();
                    }}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 800,
                      borderRadius: 999,
                      bgcolor: ACCENT,
                      color: '#000',
                      '&:hover': { bgcolor: ACCENT, opacity: 0.92 },
                    }}
                  >
                    New request
                  </Button>
                </Box>
              )}
            </Box>

            <Typography
              variant="caption"
              align="center"
              component="div"
              sx={{
                py: 0.75,
                fontSize: '0.65rem',
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                color: 'text.disabled',
                bgcolor: isDark ? alpha('#000', 0.15) : alpha('#000', 0.03),
              }}
            >
              {supportCopy.footer}
            </Typography>
          </Paper>
      </Slide>

      <Box
        sx={{
          position: 'fixed',
          left: fabPosition.x,
          top: fabPosition.y,
          width: FAB_SIZE,
          height: FAB_SIZE,
          zIndex: zFab,
        }}
      >
        <Tooltip
          title={open ? supportCopy.fabOpenLabel : supportCopy.fabClosedTooltip}
          placement="left"
          arrow
        >
          <Fab
            aria-label={open ? supportCopy.fabOpenLabel : supportCopy.fabClosedLabel}
            onClick={handleFabClick}
            onMouseDown={beginDrag}
            onTouchStart={beginDrag}
            sx={{
              width: FAB_SIZE,
              height: FAB_SIZE,
              background: open
                ? `linear-gradient(145deg, ${alpha('#64748b', 0.95)} 0%, #475569 100%)`
                : `linear-gradient(145deg, ${ACCENT} 0%, ${ACCENT_DEEP} 55%, #14b8a6 100%)`,
              color: open ? '#fff' : '#000',
              boxShadow: open
                ? `0 6px 20px ${alpha('#000', 0.25)}`
                : `0 8px 28px ${alpha(ACCENT, 0.5)}`,
              '@keyframes fabGlow': {
                '0%, 100%': { boxShadow: `0 8px 28px ${alpha(ACCENT, 0.45)}` },
                '50%': { boxShadow: `0 10px 36px ${alpha(ACCENT, 0.65)}` },
              },
              ...(!open && {
                animation: 'fabGlow 3s ease-in-out infinite',
              }),
              '&:hover': {
                transform: 'scale(1.06)',
              },
              cursor: 'grab',
              '&:active': {
                cursor: 'grabbing',
              },
              transition: 'transform 0.2s ease, background 0.25s ease, box-shadow 0.2s ease',
            }}
          >
            {open ? <CloseIcon sx={{ fontSize: 28 }} /> : <SupportAgentIcon sx={{ fontSize: 28 }} />}
          </Fab>
        </Tooltip>
      </Box>
    </>
  );
}
